"use client";

import React, { useEffect, useRef, useState } from "react";
import { Play, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function EmpoweringYourStrategicGrowth() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="230301" id="powering-your-world" className="relative w-full overflow-hidden py-20 lg:py-32 dark bg-background">
      {}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.15] mix-blend-overlay bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/electrical_pulse_grid_pattern.png')] bg-repeat" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center lg:flex-row lg:items-stretch">
          
          {}
          <div ref={textRef} className={`flex w-full flex-col justify-center lg:w-[60%] lg:pr-12 xl:pr-24 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h1 className="font-default text-[clamp(3rem,7vw,5rem)] font-bold leading-[1.05] tracking-tighter text-foreground">
              Powering Your World with{" "}
              <span className="bg-gradient-to-r from-ring to-blue-400 bg-clip-text text-transparent drop-shadow-sm !opacity-[1]">
                Precision
              </span>
            </h1>
            
            <p className="mt-8 max-w-2xl font-default text-lg leading-relaxed text-muted-foreground md:text-xl">AD Electrical delivers modern, professional electrical <br />solutions for domestic and commercial projects. From emergency callouts to full rewiring, we ensure safety and excellence in every circuit.</p>
            
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="h-14 rounded-md px-8 text-base font-default shadow-lg hover:shadow-ring/20 transition-shadow bg-ring text-white">
                <Link to="mailto:info@adelectricals.ie">
                  Get a Quote <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-14 rounded-md px-8 text-base font-default border-border hover:bg-accent hover:text-accent-foreground transition-colors">
                <Link to="tel:+353894000413">
                  <Phone className="mr-2 size-5 text-ring" /> Call Now
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="h-14 rounded-md px-8 text-base font-default border-border hover:bg-accent hover:text-accent-foreground transition-colors">
                <Link to="/ContactPage">
                  <Play className="mr-2 size-5 text-ring" /> Contact Us
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative mt-16 w-full lg:mt-0 lg:w-[40%] transition-all duration-1000 delay-300 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            {}
            <div className="relative z-20 w-full overflow-hidden rounded-lg shadow-3xl lg:-ml-16 xl:-ml-24 border border-border/50 bg-muted">
              <AspectRatio ratio={3 / 4}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/modern_architectural_lighting_installation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/modern_architectural_lighting_installation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/modern_architectural_lighting_installation.png-wvc-sizes" alt="Modern architectural lighting installation representing electrical precision" className="h-full w-full object-cover transition-transform duration-1000 !bg-[rgba(0,0,0,1)] hover:scale-105" loading="lazy" src="https://adelectricals.ie/wp-content/uploads/2026/06/ChatGPT-Image-May-18-2026-06_05_54-PM.png" />
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-ring/10 pointer-events-none" />
              </AspectRatio>
            </div>
            
            {}
            <div className="absolute -bottom-8 -right-8 -z-10 h-full w-full rounded-lg bg-ring/10 blur-3xl lg:-bottom-12 lg:-right-12" />
          </div>

        </div>
      </div>
    </section>;
}